import { useRef } from 'react';

import { TradeDetailListSection } from './styles';

import { useExchangeStore } from '@/app/hooks/trade/exchangeStore';
import { useTradeModalStore } from '@/app/hooks/trade/modalStore.tsx';
import EmptyPosition from '@/app/components/EmptyPosition';
import { AuthCheckText } from '@app/components';
import { COLORS } from '@app/constants';
import { useToken } from '@app/hooks';
import { useFetcher } from '@app/hooks/fetcher';
import EntryTime from '@components/EntryTime.tsx';
import KrwPrice from '@components/KrwPrice.tsx';
import { Modal, Table } from 'antd';
import BigNumber from 'bignumber.js';
import { useTranslation } from 'react-i18next';
import ContractsColumn from '../Columns/ContractsColumn';
import useScrollLayout from '../useScrollLayout';
import ShareModalContent from './ShareModalContent';
import TradeDetailError from './TradeDetailError';
import TradeDetailListItem from './TradeDetailListItem';

// 헤더 스타일 (TradePositionsTable과 동일)
const headerStyle = {
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
  color: '#b3b5bd',
  letterSpacing: '-0.24px',
  whiteSpace: 'nowrap' as const,
};

// 셀 데이터 공통 스타일
const cellStyle = {
  letterSpacing: 0,
  minWidth: 90,
  whiteSpace: 'nowrap' as const,
  height: 17,
  alignItems: 'flex-end' as const,
  color: '#fff',
  fontFamily: 'NanumSquareRound',
  fontSize: 12,
};

// 숫자 강조 스타일
const cellStyleBold = {
  fontWeight: 700,
  fontFamily: 'NanumSquareRound',
  letterSpacing: 0,
  whiteSpace: 'nowrap' as const,
  color: '#fff',
  lineHeight: 'normal',
  fontSize: 13,
};

const TradeFilledTable = ({ parentHeight }) => {
  const { data: token } = useToken();
  const { data: tradeModalStore, mutate: muateTradeModalStore } =
    useTradeModalStore();

  const { t } = useTranslation();
  const { EPs } = useExchangeStore();

  const KEYURL = '/trade/orderlist?status=2&ep_id=*';

  const { data, isLoading, isError } = useFetcher(KEYURL, false);

  const list = data?.result?.result?.list;
  const containerRef = useRef<HTMLDivElement>(null);
  const { needsScroll, headerHeight } = useScrollLayout(
    containerRef,
    list,
    parentHeight
  );

  const dataType = [
    'contracts',
    'amount',
    'filledPrice',
    'orderPrice',
    'orderType',
    'margin',
    'fee',
    'orderTime',
  ];

  const renderItem = (item: any, index: any) => {
    return (
      <TradeDetailListItem
        item={item}
        index={index}
        dataType={dataType.length}
      />
    );
  };

  const columns = [
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('contracts')}</span>,
      render: (value, data) => (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
          }}
        >
          <ContractsColumn item={data} />
        </div>
      ),
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('amount')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const coin = data?.pairs?.split('/')[0];
        return (
          <div style={{ ...cellStyleBold }}>
            {BigNumber(data?.amount)
              .multipliedBy(data?.leverage)
              .toFormat(EP?.dp2 || 3)}{' '}
            {coin}
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderPrice')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const dp = EP?.dp || 3;

        return (
          <div style={{ ...cellStyle }}>
            {data?.order_price === 999999999 || data?.order_price === 0
              ? '-'
              : BigNumber(data?.order_price).toFormat(dp)}
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('conclusionPrice')}</span>,
      render: (value, data) => {
        const EP = EPs?.find((ep: any) => +ep.id === +data?.exchange_pairs_id);
        const dp = EP?.dp || 3;
        const price = data?.trade_price || data?.order_price;

        return (
          <div style={{ ...cellStyle }}>{BigNumber(price).toFormat(dp)}</div>
        );
      },
    },
    // {
    //   dataIndex: '',
    //   title: t('orderPrice'),
    //   render: (value, data) => {
    //     return (
    //      <></>
    //     )
    //   },
    // },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderType')}</span>,
      render: (value, data) => {
        return (
          <div style={{ ...cellStyle }}>
            {data?.limit_market === 'L' ? t('limit') : t('market')}
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('margin')}</span>,
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        return (
          <div style={{ ...cellStyle }}>
            {BigNumber(data?.margin).toFormat(2)} {currency}
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('PNL')}</span>,
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        const pnlValue = BigNumber(data?.pnl_price);
        const isPositive = pnlValue.isGreaterThan(0);
        const isZero = pnlValue.isZero();

        return (
          <div
            style={{
              ...cellStyle,
              color: isZero
                ? '#FFFFFF'
                : isPositive
                  ? COLORS.lightGreen
                  : COLORS.lightRed,
            }}
          >
            {pnlValue.toFormat(2)} {currency}
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('fee')}</span>,
      render: (value, data) => {
        const currency = data?.pairs?.split('/')[1];
        return (
          <div style={{ ...cellStyle, minWidth: 80 }}>
            <KrwPrice
              dp={2}
              price={-BigNumber(data?.fee_price).toNumber()}
              currency={currency}
              equiv={false}
            />
          </div>
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('orderTime')}</span>,
      render: (value, data) => {
        return (
          <EntryTime
            style={{ ...cellStyle, justifyContent: 'left' }}
            time={data?.created_at}
          />
        );
      },
    },
    {
      width: '10%',
      dataIndex: '',
      title: <span style={headerStyle}>{t('executionType')}</span>,
      render: (value, data) => {
        const getExecutionType = (force) => {
          switch (force) {
            case 1:
              return t('forcedLiquidation');
            case 2:
              return 'TP (Take Profit)';
            case 3:
              return 'SL (Stop Loss)';
            default:
              return '-';
          }
        };

        return (
          <div style={{ ...cellStyle }}>{getExecutionType(data?.force)}</div>
        );
      },
    },
  ];

  return (
    <TradeDetailListSection ref={containerRef}>
      {/*<TradeDetailListHeader dataType={dataType} />*/}
      <Modal
        open={tradeModalStore?.tradeShareModal}
        onCancel={() =>
          muateTradeModalStore({ ...tradeModalStore, tradeShareModal: false })
        }
        footer={null}
        width={500}
        centered
      >
        <ShareModalContent />
      </Modal>
      {isError ? <TradeDetailError /> : null}

      {token ? null : <AuthCheckText />}

      {list?.length ? (
        <Table
          columns={columns}
          dataSource={list}
          pagination={false}
          style={{ width: '100%', height: '100%' }}
          sticky={true}
          size="small"
          scroll={
            needsScroll
              ? {
                  x: 'max-content',
                  y: parentHeight - 20 - (headerHeight || 38),
                }
              : {
                  x: 'max-content',
                }
          }
        />
      ) : (
        <EmptyPosition type="tradeHistory" />
      )}

      {/*{list?.length ? <CFlatList data={list} renderItem={renderItem} /> : null}*/}
    </TradeDetailListSection>
  );
};

export default TradeFilledTable;
